import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Cpu, Wrench, Code, Activity, Settings, Zap, Truck, Shield, Calendar, Target, Check } from "lucide-react";
import PageHeader from "@/components/common/PageHeader";
import { Reveal, stagger, fadeUp } from "@/components/common/Reveal";
import { SERVICES, CONTROLLERS, PROCESS } from "@/data/content";

const ICONS = { cpu: Cpu, wrench: Wrench, code: Code, activity: Activity, settings: Settings, zap: Zap, truck: Truck, shield: Shield, calendar: Calendar, target: Target };

const Services = () => (
  <div>
    <PageHeader
      eyebrow="Engineering Services · FANUC &amp; Siemens"
      title="Complete machine-tool engineering, under one roof"
      subtitle="From control retrofits to lifetime maintenance — a certified capability built around FANUC and Siemens control platforms."
    />

    {/* Services grid — compact */}
    <section className="py-8 md:py-12">
      <div className="max-w-[1200px] mx-auto px-6 md:px-10">
        <motion.div variants={stagger} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-60px" }}
          className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {SERVICES.map((s) => {
            const Icon = ICONS[s.icon] || Cpu;
            return (
              <motion.div key={s.slug} variants={fadeUp} className="group relative bg-white border border-[#0055FF]/30 rounded-2xl p-6 card-hover" data-testid={`service-${s.slug}`}>
                <div className="w-10 h-10 rounded-lg bg-[#0055FF]/10 border border-[#0055FF]/25 flex items-center justify-center"><Icon className="w-5 h-5 text-[#0055FF]" /></div>
                <h3 className="mt-4 text-lg text-slate-900">{s.title}</h3>
                <p className="mt-2 text-dim text-sm leading-relaxed">{s.desc}</p>
                <Link to="/quote" className="mt-4 inline-flex items-center gap-1.5 text-sm text-[#0055FF] font-medium">Request a quote <ArrowRight className="w-3.5 h-3.5" /></Link>
              </motion.div>
            );
          })}
        </motion.div>
      </div>
    </section>

    {/* FANUC & Siemens merged content */}
    <section className="py-8 md:py-12 border-t border-slate-200 bg-slate-50" data-testid="controllers-block">
      <div className="max-w-[1200px] mx-auto px-6 md:px-10">
        <div className="mb-10 text-center">
          <span className="eyebrow">Control Partners</span>
          <h2 className="mt-3 text-2xl md:text-3xl text-slate-900">We use FANUC &amp; Siemens CNC controllers</h2>
          <p className="mt-3 text-dim text-sm max-w-2xl mx-auto">Every retrofit and machine is built around FANUC or Siemens control platforms — with assistance and support provided directly from the FANUC and Siemens support teams.</p>
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          {CONTROLLERS.map((c) => (
            <Reveal key={c.brand} className="bg-white border border-[#0055FF]/30 rounded-2xl overflow-hidden card-hover" data-testid={`controller-${c.brand.toLowerCase()}`}>
              <div className="flex items-center gap-4 p-5 border-b border-slate-200 bg-slate-50">
                <div className={`w-20 h-20 shrink-0 rounded-xl flex items-center justify-center ${c.brand === "FANUC" ? "bg-[#111]" : "bg-white border border-slate-200"}`}>
                  <span className="font-display font-black tracking-tight text-xl" style={{ color: c.brand === "FANUC" ? "#FFCC00" : "#009999" }}>{c.brand}</span>
                </div>
                <div>
                  <h3 className="font-display text-xl text-slate-900">{c.brand === "SIEMENS" ? "Siemens SINUMERIK" : "FANUC Systems"}</h3>
                  <p className="text-sm text-dim">{c.tagline}</p>
                </div>
              </div>
              <div className="p-5">
                <p className="text-sm text-dim leading-relaxed">{c.desc}</p>
                <div className="mt-4 grid sm:grid-cols-2 gap-4">
                  <div>
                    <h4 className="text-[11px] uppercase tracking-widest text-slate-500 mb-2">Platforms</h4>
                    <ul className="space-y-1">{c.series.map((s) => <li key={s} className="text-xs text-slate-700">{s}</li>)}</ul>
                  </div>
                  <div>
                    <h4 className="text-[11px] uppercase tracking-widest text-slate-500 mb-2">Capabilities</h4>
                    <ul className="space-y-1">{c.points.map((p) => <li key={p} className="flex items-start gap-1.5 text-xs text-slate-700"><Check className="w-3.5 h-3.5 text-[#0055FF] mt-0.5 shrink-0" /> {p}</li>)}</ul>
                  </div>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    {/* Process */}
    <section className="py-8 md:py-12 border-t border-slate-200">
      <div className="max-w-[1200px] mx-auto px-6 md:px-10">
        <span className="eyebrow">How We Work</span>
        <h2 className="mt-3 text-2xl md:text-3xl mb-8 text-slate-900">A proven delivery process</h2>
        <div className="grid md:grid-cols-5 gap-4">
          {PROCESS.map((p, i) => (
            <Reveal key={p.step} delay={i * 0.05} className="border border-[#0055FF]/30 bg-white rounded-2xl p-5 card-hover">
              <span className="font-display text-2xl text-[#0055FF]">{p.step}</span>
              <h3 className="mt-2 text-base text-slate-900">{p.title}</h3>
              <p className="mt-1 text-dim text-sm">{p.desc}</p>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    <section className="py-8 md:py-12 text-center border-t border-slate-200 bg-slate-50">
      <Reveal>
        <h2 className="text-2xl md:text-3xl max-w-2xl mx-auto px-6 text-slate-900">Not sure which service you need? Our engineers will guide you.</h2>
        <Link to="/enquiry" className="btn-primary mt-6 !text-sm">Send an Enquiry <ArrowRight className="w-4 h-4" /></Link>
      </Reveal>
    </section>
  </div>
);

export default Services;
